import { useCallback, useEffect, useRef, useState } from 'react';
import { getSessionOriginKey, isCurrentSessionOrigin } from '@/services/sessionOrigin';
import { useUiStore, type AdvisorApprovalTarget } from '@/stores/uiStore';
import { aiChatApi } from './aiChat.api';
import type { AiChatApproval } from './aiChat.types';

type ResolvedTarget = {
  target: AdvisorApprovalTarget;
  item: AiChatApproval & { threadId: string };
};

/** Exact pending-tool reads retain the server's owner, tenant and execution authority. */
export function useTargetedChatApproval({
  open,
  canRead,
  actorKey,
  activeThreadId,
  setActiveThreadId,
  approvals,
}: {
  open: boolean;
  canRead: boolean;
  actorKey: string;
  activeThreadId: string | null;
  setActiveThreadId: (id: string) => void;
  approvals: AiChatApproval[] | undefined;
}) {
  const target = useUiStore((state) => state.advisorApprovalTarget);
  const clearTarget = useUiStore((state) => state.clearAdvisorApprovalTarget);
  const [resolved, setResolved] = useState<ResolvedTarget | null>(null);
  const [failure, setFailure] = useState<{ target: AdvisorApprovalTarget; message: string } | null>(
    null,
  );
  const source = getSessionOriginKey();
  const live = useRef({ open, canRead, actorKey, target });
  live.current = { open, canRead, actorKey, target };
  const valid = Boolean(open && canRead && target && target.sessionOrigin === source);
  const selected = valid && resolved?.target === target ? resolved.item : null;

  useEffect(() => {
    if (!target) return;
    if (!open || !canRead || !isCurrentSessionOrigin(target.sessionOrigin)) {
      clearTarget();
      return;
    }
    let current = true;
    setResolved(null);
    setFailure(null);
    void aiChatApi
      .listApprovals({ toolCallId: target.toolCallId })
      .then((result) => {
        if (!current || !isCurrentSessionOrigin(target.sessionOrigin)) return;
        const item =
          result.ok &&
          result.data?.find(
            (candidate) =>
              candidate.kind === 'tool_call' &&
              candidate.id === target.toolCallId &&
              candidate.threadId,
          );
        if (!item || !item.threadId) {
          setFailure({
            target,
            message: 'This tool is no longer pending or is unavailable to this account.',
          });
          return;
        }
        setActiveThreadId(item.threadId);
        setResolved({ target, item: { ...item, threadId: item.threadId } });
      })
      .catch(() => {
        if (current && isCurrentSessionOrigin(target.sessionOrigin)) {
          setFailure({
            target,
            message:
              'Unable to load the selected AI chat tool. Close chat and try Review in AI chat again.',
          });
        }
      });
    return () => {
      current = false;
    };
  }, [open, canRead, actorKey, target, source, clearTarget, setActiveThreadId]);

  useEffect(() => {
    if (selected && activeThreadId !== selected.threadId) clearTarget();
  }, [selected, activeThreadId, clearTarget]);

  const focusSelected = useCallback(
    (element: HTMLLIElement | null) => {
      if (
        !element ||
        !selected ||
        !resolved ||
        live.current.target !== resolved?.target ||
        !live.current.open ||
        !live.current.canRead ||
        !isCurrentSessionOrigin(resolved.target.sessionOrigin)
      )
        return;
      element.scrollIntoView?.({ block: 'nearest' });
      element.focus({ preventScroll: true });
    },
    [selected, resolved],
  );
  const clearSelection = useCallback(() => {
    if (target) clearTarget();
  }, [target, clearTarget]);

  // Never re-expose a denied/stale selected tool through the general cached list.
  const other = (approvals ?? []).filter(
    (item) => !target || item.kind !== 'tool_call' || item.id !== target.toolCallId,
  );
  return {
    items: selected ? [selected, ...other].slice(0, 4) : other.slice(0, 4),
    focusId: selected?.id ?? null,
    targetThreadId: selected?.threadId ?? null,
    notice:
      valid && !selected
        ? failure?.target === target
          ? failure.message
          : 'Loading selected AI chat tool…'
        : null,
    focusSelected,
    clearTarget: clearSelection,
  };
}
